import _ from 'underscore';
import Backbone from "backbone";
import RSVP from "rsvp";

const _SIMPLE_DB_STORAGE_DB_NAME = "trebbleSimpleDBStorageDB";
const _STORAGE_DB_VERSION = 1;
const DEFAULT_TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

// Cache type constants
const CACHE_TYPES = {
    VOICE_ACTIVITY: 'voice_activity',
    ENHANCED_CUT_POINT: 'enhanced_cut_point'
};

let storageDBFailedInitialization = false;
let instance = null;

const SimpleDBStorageHelper = Backbone.Model.extend({
    // Initialize arrays as class properties to prevent race conditions
    listenersReady: [],
    listenersReadyFailed: [],
    _cleanupIntervalId: null,
    
    initialize: function() {
        console.log("SimpleDBStorageHelper: Initializing");
        // Ensure arrays are properly initialized for this instance
        this.listenersReady = [];
        this.listenersReadyFailed = [];
        this._database = null;
        this._memoryStorage = {}; // In-memory fallback storage
        this._usingMemoryFallback = false;
        this.initializeDatabase();
    },
    
    initializeDatabase: function() {
        console.log("SimpleDBStorageHelper: Opening IndexedDB");
        try {
            if (!window.indexedDB) {
                console.warn("SimpleDBStorageHelper: IndexedDB not available, using in-memory storage");
                storageDBFailedInitialization = true;
                this._usingMemoryFallback = true;
                this.notifyListeners(); // Still notify success, but using memory fallback
                return;
            }
            
            const request = window.indexedDB.open(_SIMPLE_DB_STORAGE_DB_NAME, _STORAGE_DB_VERSION);
            
            request.onerror = (function(event) {
                console.error("SimpleDBStorageHelper: Error opening database:", event.target.error);
                console.log("SimpleDBStorageHelper: Falling back to in-memory storage");
                storageDBFailedInitialization = true;
                this._usingMemoryFallback = true;
                this.notifyListeners(); // Still notify success, but using memory fallback
            }).bind(this);
            
            request.onsuccess = (function(event) {
                console.log("SimpleDBStorageHelper: Database opened successfully");
                this._database = event.target.result;
                this.notifyListeners();
                
                // Start periodic cleanup
                this.scheduleCleanup();
            }).bind(this);
            
            request.onupgradeneeded = (function(event) {
                console.log("SimpleDBStorageHelper: Database needs upgrade, creating object stores");
                const db = event.target.result;
                
                if (!db.objectStoreNames.contains("simpleStorage")) {
                    const objectStore = db.createObjectStore("simpleStorage", { keyPath: "key" });
                    objectStore.createIndex("type", "type", { unique: false });
                    objectStore.createIndex("expiresAt", "expiresAt", { unique: false });
                    objectStore.createIndex("lastAccessed", "lastAccessed", { unique: false });
                    console.log("SimpleDBStorageHelper: Created simpleStorage object store with indexes");
                }
            }).bind(this);
            
        } catch (err) {
            console.error("SimpleDBStorageHelper: Failed opening DB:", err);
            console.log("SimpleDBStorageHelper: Falling back to in-memory storage");
            storageDBFailedInitialization = true;
            this._usingMemoryFallback = true;
            this.notifyListeners(); // Still notify success, but using memory fallback
        }
    },
    
    isReady: function() {
        if (this._database || this._usingMemoryFallback) {
            console.log("SimpleDBStorageHelper: Storage is ready (IndexedDB: " + !!this._database + ", Memory: " + this._usingMemoryFallback + ")");
            return RSVP.Promise.resolve();
        } else {
            console.log("SimpleDBStorageHelper: Storage is not ready yet, waiting...");
            return new RSVP.Promise((function(resolve, reject) {
                console.log("SimpleDBStorageHelper: Adding to listeners list");
                // Ensure arrays exist before pushing
                if (!this.listenersReady) {
                    this.listenersReady = [];
                }
                if (!this.listenersReadyFailed) {
                    this.listenersReadyFailed = [];
                }
                this.listenersReady.push(resolve);
                this.listenersReadyFailed.push(reject);
            }).bind(this));
        }
    },
    
    notifyListeners: function() {
        if (!this.listenersReady) {
            this.listenersReady = [];
        }
        console.log("SimpleDBStorageHelper: Notifying " + this.listenersReady.length + " listeners");
        if (this.listenersReady) {
            for (let i = 0; i < this.listenersReady.length; i++) {
                this.listenersReady[i]();
            }
            this.listenersReady = [];
        }
    },
    
    notifyListenersFailed: function(error) {
        if (!this.listenersReadyFailed) {
            this.listenersReadyFailed = [];
        }
        console.log("SimpleDBStorageHelper: Notifying " + this.listenersReadyFailed.length + " listeners of failure");
        if (this.listenersReadyFailed) {
            for (let i = 0; i < this.listenersReadyFailed.length; i++) {
                this.listenersReadyFailed[i](error);
            }
            this.listenersReadyFailed = [];
        }
    },
    
    getTransaction: function(storeNames, mode) {
        if (!this._database) {
            console.warn("SimpleDBStorageHelper: Database not ready for transaction");
            return null;
        }
        try {
            return this._database.transaction(storeNames, mode);
        } catch (error) {
            console.error("SimpleDBStorageHelper: Transaction creation failed:", error);
            return null;
        }
    },
    
    getFromObjectStore: function(objectStore, key) {
        return new RSVP.Promise(function(resolve, reject) {
            const request = objectStore.get(key);
            request.onsuccess = function() {
                resolve(request.result);
            };
            request.onerror = function() {
                reject(request.error);
            };
        });
    },
    
    putToObjectStore: function(objectStore, data) {
        return new RSVP.Promise(function(resolve, reject) {
            const request = objectStore.put(data);
            request.onsuccess = function() {
                resolve(request.result);
            };
            request.onerror = function() {
                reject(request.error);
            };
        });
    },
    
    deleteFromObjectStore: function(objectStore, key) {
        return new RSVP.Promise(function(resolve, reject) {
            const request = objectStore.delete(key);
            request.onsuccess = function() {
                resolve(request.result);
            };
            request.onerror = function() {
                reject(request.error);
            };
        });
    },
    
    generateCacheKey: function(type, identifier) {
        return type + "_" + identifier;
    },
    
    set: function(cacheType, identifier, data, options = {}) {
        return this.isReady().then((function() {
            try {
                const now = Date.now();
                const ttl = options.ttl || DEFAULT_TTL_MS;
                const key = this.generateCacheKey(cacheType, identifier);
                
                const cacheEntry = {
                    key: key,
                    type: cacheType,
                    data: data,
                    createdAt: now,
                    lastAccessed: now,
                    expiresAt: now + ttl,
                    ttl: ttl
                };
                
                // Determine storage method upfront to avoid race conditions
                const useMemoryFallback = this._usingMemoryFallback || !this._database;
                
                return this._performSet(cacheEntry, useMemoryFallback);
            } catch (error) {
                console.error("SimpleDBStorageHelper: Set operation failed completely:", error);
                return RSVP.Promise.resolve(); // Silent failure, don't break application
            }
        }).bind(this)).catch((function(error) {
            console.error("SimpleDBStorageHelper: Set operation failed (not ready):", error);
            return RSVP.Promise.resolve(); // Silent failure, don't break application
        }).bind(this));
    },
    
    _performSet: function(cacheEntry, useMemoryFallback) {
        if (useMemoryFallback) {
            console.log("SimpleDBStorageHelper: Storing in memory:", cacheEntry.key);
            this._memoryStorage[cacheEntry.key] = cacheEntry;
            return RSVP.Promise.resolve();
        } else {
            try {
                const transaction = this.getTransaction(["simpleStorage"], "readwrite");
                if (!transaction) {
                    console.warn("SimpleDBStorageHelper: Transaction creation failed for set, falling back to memory");
                    this._memoryStorage[cacheEntry.key] = cacheEntry;
                    return RSVP.Promise.resolve();
                }
                const objectStore = transaction.objectStore("simpleStorage");
                return this.putToObjectStore(objectStore, cacheEntry).catch((function(indexedDBError) {
                    console.warn("SimpleDBStorageHelper: IndexedDB set failed, falling back to memory:", indexedDBError);
                    this._memoryStorage[cacheEntry.key] = cacheEntry;
                    return RSVP.Promise.resolve();
                }).bind(this));
            } catch (transactionError) {
                console.warn("SimpleDBStorageHelper: Transaction creation failed, falling back to memory:", transactionError);
                this._memoryStorage[cacheEntry.key] = cacheEntry;
                return RSVP.Promise.resolve();
            }
        }
    },
    
    _updateLastAccessed: function(key, timestamp) {
        try {
            const transaction = this.getTransaction(["simpleStorage"], "readwrite");
            if (transaction) {
                const objectStore = transaction.objectStore("simpleStorage");
                this.getFromObjectStore(objectStore, key).then((function(entry) {
                    if (entry) {
                        entry.lastAccessed = timestamp;
                        this.putToObjectStore(objectStore, entry);
                    }
                }).bind(this)).catch(function(error) {
                    // Silent failure for lastAccessed updates - don't log to avoid spam
                });
            } else {
                // Try updating memory storage as fallback
                if (this._memoryStorage[key]) {
                    this._memoryStorage[key].lastAccessed = timestamp;
                }
            }
        } catch (error) {
            // Silent failure for lastAccessed updates
            // Try updating memory storage as fallback
            try {
                if (this._memoryStorage[key]) {
                    this._memoryStorage[key].lastAccessed = timestamp;
                }
            } catch (memoryError) {
                // Complete silent failure - lastAccessed update is not critical
            }
        }
    },
    
    get: function(cacheType, identifier) {
        return this.isReady().then((function() {
            try {
                const key = this.generateCacheKey(cacheType, identifier);
                
                if (this._usingMemoryFallback) {
                    try {
                        console.log("SimpleDBStorageHelper: Getting from memory:", key);
                        const cacheEntry = this._memoryStorage[key];
                        
                        if (!cacheEntry) {
                            return null;
                        }
                        
                        const now = Date.now();
                        
                        // Check if expired
                        if (cacheEntry.expiresAt && now > cacheEntry.expiresAt) {
                            console.log("SimpleDBStorageHelper: Memory cache entry expired, removing:", key);
                            delete this._memoryStorage[key];
                            return null;
                        }
                        
                        // Update last accessed time
                        cacheEntry.lastAccessed = now;
                        
                        return cacheEntry.data;
                    } catch (memoryError) {
                        console.warn("SimpleDBStorageHelper: Memory get failed:", memoryError);
                        return null;
                    }
                } else {
                    try {
                        const transaction = this.getTransaction(["simpleStorage"], "readwrite");
                        if (!transaction) {
                            console.warn("SimpleDBStorageHelper: Transaction creation failed for get, trying memory fallback");
                            // Try memory fallback if transaction fails
                            try {
                                const cacheEntry = this._memoryStorage[key];
                                if (cacheEntry && (!cacheEntry.expiresAt || Date.now() <= cacheEntry.expiresAt)) {
                                    console.log("SimpleDBStorageHelper: Using memory fallback for:", key);
                                    return cacheEntry.data;
                                }
                            } catch (fallbackError) {
                                console.warn("SimpleDBStorageHelper: Memory fallback also failed:", fallbackError);
                            }
                            return null;
                        }
                        const objectStore = transaction.objectStore("simpleStorage");
                        
                        return this.getFromObjectStore(objectStore, key).then((function(cacheEntry) {
                            if (!cacheEntry) {
                                return null;
                            }
                            
                            const now = Date.now();
                            
                            // Check if expired
                            if (cacheEntry.expiresAt && now > cacheEntry.expiresAt) {
                                console.log("SimpleDBStorageHelper: Cache entry expired, removing:", key);
                                try {
                                    this.deleteFromObjectStore(objectStore, key);
                                } catch (deleteError) {
                                    console.warn("SimpleDBStorageHelper: Failed to delete expired entry:", deleteError);
                                }
                                return null;
                            }
                            
                            // Update lastAccessed in background, don't block return
                            setTimeout((function() {
                                this._updateLastAccessed(key, now);
                            }).bind(this), 0);
                            
                            return cacheEntry.data;
                        }).bind(this)).catch((function(indexedDBError) {
                            console.warn("SimpleDBStorageHelper: IndexedDB get failed, checking memory fallback:", indexedDBError);
                            // Try memory fallback if IndexedDB fails
                            try {
                                const cacheEntry = this._memoryStorage[key];
                                if (cacheEntry && (!cacheEntry.expiresAt || Date.now() <= cacheEntry.expiresAt)) {
                                    console.log("SimpleDBStorageHelper: Using memory fallback for:", key);
                                    return cacheEntry.data;
                                }
                            } catch (fallbackError) {
                                console.warn("SimpleDBStorageHelper: Memory fallback also failed:", fallbackError);
                            }
                            return null;
                        }).bind(this));
                    } catch (transactionError) {
                        console.warn("SimpleDBStorageHelper: Transaction creation failed, trying memory fallback:", transactionError);
                        // Try memory fallback if transaction fails
                        try {
                            const cacheEntry = this._memoryStorage[key];
                            if (cacheEntry && (!cacheEntry.expiresAt || Date.now() <= cacheEntry.expiresAt)) {
                                console.log("SimpleDBStorageHelper: Using memory fallback for:", key);
                                return cacheEntry.data;
                            }
                        } catch (fallbackError) {
                            console.warn("SimpleDBStorageHelper: Memory fallback also failed:", fallbackError);
                        }
                        return null;
                    }
                }
            } catch (error) {
                console.error("SimpleDBStorageHelper: Get operation failed completely:", error);
                return null; // Silent failure, don't break application
            }
        }).bind(this)).catch((function(error) {
            console.error("SimpleDBStorageHelper: Get operation failed (not ready):", error);
            return null; // Silent failure, don't break application
        }).bind(this));
    },
    
    has: function(cacheType, identifier) {
        return this.get(cacheType, identifier).then(function(data) {
            return data !== null;
        }).catch(function(error) {
            console.error("SimpleDBStorageHelper: Has operation failed:", error);
            return false; // Silent failure, don't break application
        });
    },
    
    delete: function(cacheType, identifier) {
        return this.isReady().then((function() {
            try {
                const key = this.generateCacheKey(cacheType, identifier);
                
                if (this._usingMemoryFallback) {
                    try {
                        console.log("SimpleDBStorageHelper: Deleting from memory:", key);
                        delete this._memoryStorage[key];
                        return RSVP.Promise.resolve();
                    } catch (memoryError) {
                        console.warn("SimpleDBStorageHelper: Memory delete failed:", memoryError);
                        return RSVP.Promise.resolve(); // Silent failure
                    }
                } else {
                    try {
                        const transaction = this.getTransaction(["simpleStorage"], "readwrite");
                        if (!transaction) {
                            console.warn("SimpleDBStorageHelper: Transaction creation failed for delete, trying memory fallback");
                            try {
                                delete this._memoryStorage[key];
                            } catch (fallbackError) {
                                console.warn("SimpleDBStorageHelper: Memory fallback delete also failed:", fallbackError);
                            }
                            return RSVP.Promise.resolve();
                        }
                        const objectStore = transaction.objectStore("simpleStorage");
                        return this.deleteFromObjectStore(objectStore, key).catch((function(indexedDBError) {
                            console.warn("SimpleDBStorageHelper: IndexedDB delete failed, trying memory fallback:", indexedDBError);
                            try {
                                delete this._memoryStorage[key];
                            } catch (fallbackError) {
                                console.warn("SimpleDBStorageHelper: Memory fallback delete also failed:", fallbackError);
                            }
                            return RSVP.Promise.resolve(); // Silent failure
                        }).bind(this));
                    } catch (transactionError) {
                        console.warn("SimpleDBStorageHelper: Transaction creation failed for delete, trying memory fallback:", transactionError);
                        try {
                            delete this._memoryStorage[key];
                        } catch (fallbackError) {
                            console.warn("SimpleDBStorageHelper: Memory fallback delete also failed:", fallbackError);
                        }
                        return RSVP.Promise.resolve(); // Silent failure
                    }
                }
            } catch (error) {
                console.error("SimpleDBStorageHelper: Delete operation failed completely:", error);
                return RSVP.Promise.resolve(); // Silent failure, don't break application
            }
        }).bind(this)).catch((function(error) {
            console.error("SimpleDBStorageHelper: Delete operation failed (not ready):", error);
            return RSVP.Promise.resolve(); // Silent failure, don't break application
        }).bind(this));
    },
    
    cleanup: function() {
        return this.isReady().then((function() {
            console.log("SimpleDBStorageHelper: Starting cleanup of expired entries");
            const now = Date.now();
            
            if (this._usingMemoryFallback) {
                let deletedCount = 0;
                const keysToDelete = [];
                
                // Find expired entries
                for (const key in this._memoryStorage) {
                    if (Object.prototype.hasOwnProperty.call(this._memoryStorage, key)) {
                        const entry = this._memoryStorage[key];
                        if (entry.expiresAt && now > entry.expiresAt) {
                            keysToDelete.push(key);
                        }
                    }
                }
                
                // Delete expired entries
                keysToDelete.forEach((function(key) {
                    console.log("SimpleDBStorageHelper: Deleting expired memory entry:", key);
                    delete this._memoryStorage[key];
                    deletedCount++;
                }).bind(this));
                
                console.log("SimpleDBStorageHelper: Memory cleanup complete, deleted " + deletedCount + " entries");
                return RSVP.Promise.resolve(deletedCount);
            } else {
                const transaction = this.getTransaction(["simpleStorage"], "readwrite");
                if (!transaction) {
                    console.warn("SimpleDBStorageHelper: Transaction creation failed for cleanup, skipping IndexedDB cleanup");
                    return RSVP.Promise.resolve(0);
                }
                const objectStore = transaction.objectStore("simpleStorage");
                const expiresAtIndex = objectStore.index("expiresAt");
                
                return new RSVP.Promise(function(resolve, reject) {
                    const range = IDBKeyRange.upperBound(now);
                    const request = expiresAtIndex.openCursor(range);
                    let deletedCount = 0;
                    
                    request.onsuccess = function(event) {
                        const cursor = event.target.result;
                        if (cursor) {
                            console.log("SimpleDBStorageHelper: Deleting expired entry:", cursor.value.key);
                            cursor.delete();
                            deletedCount++;
                            cursor.continue();
                        } else {
                            console.log("SimpleDBStorageHelper: Cleanup complete, deleted " + deletedCount + " entries");
                            resolve(deletedCount);
                        }
                    };
                    
                    request.onerror = function() {
                        console.error("SimpleDBStorageHelper: Cleanup failed:", request.error);
                        reject(request.error);
                    };
                });
            }
        }).bind(this));
    },
    
    clearExpired: function() {
        return this.cleanup();
    },
    
    _fallbackToMemory: function(reason) {
        if (!this._usingMemoryFallback) {
            console.warn("SimpleDBStorageHelper: Falling back to memory storage due to:", reason);
            this._usingMemoryFallback = true;
            this._database = null;
            // Don't close the database connection as it might be used by other operations
        }
    },
    
    clearAll: function() {
        return this.isReady().then((function() {
            try {
                console.log("SimpleDBStorageHelper: Clearing all cached data");
                
                if (this._usingMemoryFallback) {
                    try {
                        // Clear memory storage
                        const deletedCount = Object.keys(this._memoryStorage).length;
                        this._memoryStorage = {};
                        console.log("SimpleDBStorageHelper: Cleared " + deletedCount + " entries from memory storage");
                        return RSVP.Promise.resolve(deletedCount);
                    } catch (memoryError) {
                        console.warn("SimpleDBStorageHelper: Memory clearAll failed:", memoryError);
                        return RSVP.Promise.resolve(0); // Silent failure
                    }
                } else {
                    try {
                        // Clear IndexedDB storage
                        const transaction = this.getTransaction(["simpleStorage"], "readwrite");
                        if (!transaction) {
                            console.warn("SimpleDBStorageHelper: Transaction creation failed for clearAll, trying memory fallback");
                            try {
                                const deletedCount = Object.keys(this._memoryStorage).length;
                                this._memoryStorage = {};
                                console.log("SimpleDBStorageHelper: Cleared " + deletedCount + " entries from memory fallback");
                                return RSVP.Promise.resolve(deletedCount);
                            } catch (fallbackError) {
                                console.warn("SimpleDBStorageHelper: Memory fallback clearAll also failed:", fallbackError);
                                return RSVP.Promise.resolve(0);
                            }
                        }
                        const objectStore = transaction.objectStore("simpleStorage");
                        
                        return new RSVP.Promise((function(resolve, reject) {
                            const request = objectStore.clear();
                            
                            request.onsuccess = function() {
                                console.log("SimpleDBStorageHelper: Cleared all entries from IndexedDB");
                                // Also clear memory fallback to be safe
                                try {
                                    this._memoryStorage = {};
                                } catch (memoryError) {
                                    console.warn("SimpleDBStorageHelper: Failed to clear memory during clearAll:", memoryError);
                                }
                                resolve();
                            }.bind(this);
                            
                            request.onerror = (function() {
                                console.error("SimpleDBStorageHelper: Failed to clear IndexedDB, trying memory fallback:", request.error);
                                try {
                                    const deletedCount = Object.keys(this._memoryStorage).length;
                                    this._memoryStorage = {};
                                    console.log("SimpleDBStorageHelper: Cleared " + deletedCount + " entries from memory fallback");
                                    resolve(deletedCount);
                                } catch (fallbackError) {
                                    console.warn("SimpleDBStorageHelper: Memory fallback clearAll also failed:", fallbackError);
                                    resolve(0); // Silent failure
                                }
                            }).bind(this);
                        }).bind(this));
                    } catch (transactionError) {
                        console.warn("SimpleDBStorageHelper: Transaction creation failed for clearAll, trying memory fallback:", transactionError);
                        try {
                            const deletedCount = Object.keys(this._memoryStorage).length;
                            this._memoryStorage = {};
                            console.log("SimpleDBStorageHelper: Cleared " + deletedCount + " entries from memory fallback");
                            return RSVP.Promise.resolve(deletedCount);
                        } catch (fallbackError) {
                            console.warn("SimpleDBStorageHelper: Memory fallback clearAll also failed:", fallbackError);
                            return RSVP.Promise.resolve(0); // Silent failure
                        }
                    }
                }
            } catch (error) {
                console.error("SimpleDBStorageHelper: ClearAll operation failed completely:", error);
                return RSVP.Promise.resolve(0); // Silent failure, don't break application
            }
        }).bind(this)).catch((function(error) {
            console.error("SimpleDBStorageHelper: ClearAll operation failed (not ready):", error);
            return RSVP.Promise.resolve(0); // Silent failure, don't break application
        }).bind(this));
    },
    
    scheduleCleanup: function() {
        // Only schedule cleanup if we have persistent storage or memory fallback
        if (this._database || this._usingMemoryFallback) {
            // Run cleanup once when storage is ready
            this.cleanup().catch(function(error) {
                console.error("SimpleDBStorageHelper: Initial cleanup failed:", error);
            });
            
            // Schedule periodic cleanup every 24 hours
            this._cleanupIntervalId = setInterval((function() {
                this.cleanup().catch(function(error) {
                    console.error("SimpleDBStorageHelper: Periodic cleanup failed:", error);
                });
            }).bind(this), 24 * 60 * 60 * 1000);
        }
    },
    
    getStats: function() {
        return this.isReady().then((function() {
            if (this._usingMemoryFallback) {
                const stats = {
                    totalEntries: 0,
                    expiredEntries: 0,
                    typeBreakdown: {},
                    usingMemoryFallback: true
                };
                
                const now = Date.now();
                
                for (const key in this._memoryStorage) {
                    if (Object.prototype.hasOwnProperty.call(this._memoryStorage, key)) {
                        const entry = this._memoryStorage[key];
                        stats.totalEntries++;
                        
                        if (entry.expiresAt && now > entry.expiresAt) {
                            stats.expiredEntries++;
                        }
                        
                        if (entry.type) {
                            stats.typeBreakdown[entry.type] = (stats.typeBreakdown[entry.type] || 0) + 1;
                        }
                    }
                }
                
                return RSVP.Promise.resolve(stats);
            } else {
                const transaction = this.getTransaction(["simpleStorage"], "readonly");
                if (!transaction) {
                    console.warn("SimpleDBStorageHelper: Transaction creation failed for getStats, returning empty stats");
                    return RSVP.Promise.resolve({
                        totalEntries: 0,
                        expiredEntries: 0,
                        typeBreakdown: {},
                        usingMemoryFallback: false,
                        error: "Transaction creation failed"
                    });
                }
                const objectStore = transaction.objectStore("simpleStorage");
                
                return new RSVP.Promise(function(resolve, reject) {
                    const stats = {
                        totalEntries: 0,
                        expiredEntries: 0,
                        typeBreakdown: {},
                        usingMemoryFallback: false
                    };
                    
                    const request = objectStore.openCursor();
                    const now = Date.now();
                    
                    request.onsuccess = function(event) {
                        const cursor = event.target.result;
                        if (cursor) {
                            const entry = cursor.value;
                            stats.totalEntries++;
                            
                            if (entry.expiresAt && now > entry.expiresAt) {
                                stats.expiredEntries++;
                            }
                            
                            if (entry.type) {
                                stats.typeBreakdown[entry.type] = (stats.typeBreakdown[entry.type] || 0) + 1;
                            }
                            
                            cursor.continue();
                        } else {
                            resolve(stats);
                        }
                    };
                    
                    request.onerror = function() {
                        reject(request.error);
                    };
                });
            }
        }).bind(this));
    },
    
    destroy: function() {
        if (this._cleanupIntervalId) {
            clearInterval(this._cleanupIntervalId);
            this._cleanupIntervalId = null;
        }
        if (this._database) {
            this._database.close();
            this._database = null;
        }
        this._memoryStorage = {};
        this._usingMemoryFallback = false;
        
        // Clear listeners arrays
        this.listenersReady = [];
        this.listenersReadyFailed = [];
    }
});

SimpleDBStorageHelper.getInstance = function() {
    if (!instance) {
        instance = new SimpleDBStorageHelper();
    }
    return instance;
};

SimpleDBStorageHelper.resetInstance = function() {
    if (instance && typeof instance.destroy === 'function') {
        instance.destroy();
    }
    instance = null;
};

// Export cache type constants
SimpleDBStorageHelper.CACHE_TYPES = CACHE_TYPES;

export default SimpleDBStorageHelper;